/**
 * Collection Filter by Stock Availability - ZERO API CALLS VERSION
 * Reads variant availability directly from HTML data attributes
 * NO rate limiting, NO performance issues!
 */

(function () {
    'use strict';

    const DEBUG = false; // Set to true only when debugging

    function getActiveFilters() {
        const variantFilters = [];
        const metafieldFilters = [];
        const checkboxes = document.querySelectorAll('[data-collection-filters-form] input[type="checkbox"]:checked');
        
        checkboxes.forEach(checkbox => {
            const value = checkbox.value;
            const name = checkbox.name;
            
            if (value) {
                // Check if this is a metafield filter (contains .p.m. or similar patterns)
                const isMetafield = name && (
                    name.includes('.p.m.') || 
                    name.includes('filter.p.m') ||
                    name.includes('custom.color') ||
                    name.includes('custom.leather')
                );
                
                // Normalize the value (lowercase, replace spaces with hyphens)
                const normalized = value.toLowerCase().replace(/\s+/g, '-');
                
                if (isMetafield) {
                    metafieldFilters.push({ name, value: normalized });
                    if (DEBUG) {
                        console.log(`  📋 Metafield filter (no stock check): ${name} = ${value}`);
                    }
                } else {
                    variantFilters.push(normalized);
                    if (DEBUG) {
                        console.log(`  🏷️  Variant filter (stock check): ${name} = ${value}`);
                    }
                }
            }
        });

        if (DEBUG) {
            console.log('🎯 Variant filters (will check stock):', variantFilters);
            console.log('📋 Metafield filters (Shopify handles these):', metafieldFilters);
        }

        return { variantFilters, metafieldFilters };
    }

    // Helper function to re-initialize lazy loading for a product item
    function reinitializeLazyLoading(item) {
        // Wait for lazysizes to be available
        if (typeof window.lazySizes === 'undefined') {
            console.warn('⚠️  lazysizes not available yet');
            return;
        }
        
        // Find all lazy-loaded elements in this product
        const lazyElements = item.querySelectorAll('.lazyload');
        
        lazyElements.forEach(function(el) {
            // Remove lazyloaded class to force re-check
            el.classList.remove('lazyloaded');
            
            // Force lazysizes to check this element
            if (window.lazySizes && window.lazySizes.loader) {
                window.lazySizes.loader.unveil(el, true);
            }
        });
        
        // Also handle background images with data-bgset
        const bgElements = item.querySelectorAll('[data-bgset]');
        bgElements.forEach(function(el) {
            if (!el.classList.contains('lazyloaded')) {
                if (window.lazySizes && window.lazySizes.loader) {
                    window.lazySizes.loader.unveil(el, true);
                }
            }
        });
    }
    
    // Force lazysizes to check all visible lazy elements
    function forceLazySizesCheck() {
        // Wait for next frame to ensure DOM is updated
        requestAnimationFrame(function() {
            if (typeof window.lazySizes === 'undefined') {
                // Try again after a delay (silent retry)
                setTimeout(forceLazySizesCheck, 500);
                return;
            }
            
            // Get all visible lazy elements (not hidden by our filter)
            const visibleProducts = document.querySelectorAll('.product-item:not(.hidden-by-stock-filter)');
            let totalLazyElements = 0;
            
            visibleProducts.forEach(function(product) {
                // Remove lazyloaded class to force re-check
                const lazyElements = product.querySelectorAll('.lazyload');
                lazyElements.forEach(function(el) {
                    // Only process if not already loaded
                    if (!el.classList.contains('lazyloaded')) {
                        totalLazyElements++;
                        if (window.lazySizes && window.lazySizes.loader) {
                            window.lazySizes.loader.unveil(el, true);
                        }
                    }
                });
                
                // Also handle background images
                const bgElements = product.querySelectorAll('[data-bgset]');
                bgElements.forEach(function(el) {
                    if (!el.classList.contains('lazyloaded')) {
                        totalLazyElements++;
                        if (window.lazySizes && window.lazySizes.loader) {
                            window.lazySizes.loader.unveil(el, true);
                        }
                    }
                });
            });
            
            if (DEBUG && totalLazyElements > 0) {
                console.log(`🖼️  Processed ${totalLazyElements} lazy elements`);
            }
            
            // Trigger lazysizes' internal check mechanism
            if (window.lazySizes) {
                // Force check all elements in viewport
                if (window.lazySizes.autoSizer) {
                    window.lazySizes.autoSizer.checkElems();
                }
                
                // Trigger multiple events to force re-check
                setTimeout(function() {
                    window.dispatchEvent(new Event('scroll'));
                    window.dispatchEvent(new Event('resize'));
                    // Also trigger lazysizes' custom event
                    window.dispatchEvent(new Event('lazyunveil'));
                }, 50);
            }
        });
    }

    function filterProductsByStock() {
        const { variantFilters, metafieldFilters } = getActiveFilters();

        // If no variant filters (only metafield filters), don't do stock filtering
        // Let Shopify's native filtering handle metafield filters
        if (variantFilters.length === 0) {
            console.log('ℹ️  Only metafield filters active - skipping stock filtering');
            document.querySelectorAll('.product-item').forEach(item => {
                item.style.display = '';
                item.classList.remove('hidden-by-stock-filter');
            });
            return;
        }

        if (DEBUG) console.log('✨ Filtering products by stock availability...');

        const productItems = document.querySelectorAll('.product-item');
        let hiddenCount = 0;
        let visibleCount = 0;

        productItems.forEach(item => {
            // Get the available variants data from the HTML attribute
            const availableVariants = item.getAttribute('data-available-variants');
            const title = item.querySelector('.product-link')?.getAttribute('aria-label') || 'Unknown';
            
            if (!availableVariants || availableVariants.trim() === '') {
                // No variant data = hide product (all sold out)
                item.style.display = 'none';
                item.classList.add('hidden-by-stock-filter');
                hiddenCount++;
                if (DEBUG) {
                    console.log(`❌ ${title}: No available variants`);
                }
                return;
            }

            // DEBUG: Show what variant data we have
            if (DEBUG) {
                console.log(`\n🔍 ${title}:`);
                console.log(`   Raw data: "${availableVariants}"`);
            }

            // Parse the variant data
            // Format: "36,black|38,black|40,red" (each variant separated by |, options separated by ,)
            const variantSets = availableVariants.split('|').filter(v => v.trim() !== '');
            let hasMatchingVariant = false;

            if (DEBUG) {
                console.log(`   Variant sets (${variantSets.length}):`, variantSets);
            }

            for (const variantSet of variantSets) {
                const options = variantSet.split(',').map(o => o.trim()).filter(o => o !== '');
                
                if (DEBUG) {
                    console.log(`   Checking variant: [${options.join(', ')}]`);
                }
                
                // Check if this variant has ANY of the variant filter values (OR logic for multiple selections)
                const hasAnyFilter = variantFilters.some(filter => {
                    const match = options.includes(filter);
                    if (DEBUG) {
                        console.log(`     "${filter}" in [${options.join(', ')}]? ${match}`);
                    }
                    return match;
                });

                if (hasAnyFilter) {
                    hasMatchingVariant = true;
                    if (DEBUG) {
                        console.log(`   ✅ MATCH FOUND!`);
                    }
                    break;
                }
            }

            // Show or hide based on availability
            if (hasMatchingVariant) {
                // Show the product
                const wasHidden = item.classList.contains('hidden-by-stock-filter');
                item.style.display = '';
                item.style.opacity = '';
                item.style.visibility = '';
                item.classList.remove('hidden-by-stock-filter');
                visibleCount++;
                
                // Force re-initialize lazy loading if product was previously hidden
                if (wasHidden) {
                    // Remove lazyloaded class from all lazy elements to force re-check
                    const lazyElements = item.querySelectorAll('.lazyload');
                    lazyElements.forEach(function(el) {
                        el.classList.remove('lazyloaded');
                    });
                    
                    setTimeout(function() {
                        reinitializeLazyLoading(item);
                    }, 100);
                }
                
                if (DEBUG) {
                    const title = item.querySelector('.product-link')?.getAttribute('aria-label') || 'Unknown';
                    console.log(`✅ ${title}: Has stock`);
                }
            } else {
                // Hide the product
                item.style.display = 'none';
                item.classList.add('hidden-by-stock-filter');
                hiddenCount++;
                if (DEBUG) {
                    const title = item.querySelector('.product-link')?.getAttribute('aria-label') || 'Unknown';
                    console.log(`❌ ${title}: No stock for selected filters`);
                }
            }
        });

        if (DEBUG) console.log(`📊 Results: ${visibleCount} visible, ${hiddenCount} hidden`);
        
        // Force lazysizes to check all visible products - multiple attempts
        // First attempt: after DOM updates
        requestAnimationFrame(function() {
            setTimeout(function() {
                forceLazySizesCheck();
            }, 200);
        });
        
        // Second attempt: after a longer delay
        setTimeout(function() {
            forceLazySizesCheck();
        }, 800);
        
        // Third attempt: final check
        setTimeout(function() {
            forceLazySizesCheck();
        }, 2000);
    }

    // Debounce function
    function debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
            clearTimeout(timeout);
            timeout = setTimeout(() => func(...args), wait);
        };
    }

    const debouncedFilter = debounce(filterProductsByStock, 200);

    // Check for filters on page load
    function checkInitialFilters() {
        setTimeout(function() {
            const checkboxes = document.querySelectorAll('[data-collection-filters-form] input[type="checkbox"]:checked');
            
            if (checkboxes.length > 0) {
                if (DEBUG) console.log(`✅ Found ${checkboxes.length} active filters, applying stock filter...`);
                filterProductsByStock();
            }
        }, 500);
    }

    // Initialize
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', checkInitialFilters);
    } else {
        checkInitialFilters();
    }

    // Listen for filter changes
    document.addEventListener('change', function(e) {
        if (e.target.matches('[data-collection-filters-form] input[type="checkbox"]')) {
            if (DEBUG) console.log('🔄 Filter changed, re-filtering...');
            debouncedFilter();
        }
    });

    // Listen for filter URL updates (AJAX filtering)
    document.addEventListener('click', function(e) {
        const filterLink = e.target.closest('[data-filter-update-url]');
        if (filterLink) {
            setTimeout(function() {
                const hasFilters = document.querySelectorAll('[data-collection-filters-form] input[type="checkbox"]:checked').length > 0;
                if (hasFilters) {
                    debouncedFilter();
                }
            }, 600);
        }
    });

    // Watch for product grid updates (AJAX filtering, new products added)
    const productGrid = document.querySelector('[data-products-grid]');
    if (productGrid) {
        const observer = new MutationObserver(function(mutations) {
            let productsAdded = false;
            
            mutations.forEach(function(mutation) {
                if (mutation.addedNodes.length > 0) {
                    productsAdded = true;
                }
            });
            
            if (productsAdded) {
                if (DEBUG) console.log('🔄 Product grid updated (new products added)');
                
                // Force lazysizes to check all products
                setTimeout(function() {
                    forceLazySizesCheck();
                }, 500);
            }
            
            // Check if we need to re-filter for stock
            const hasFilters = document.querySelectorAll('[data-collection-filters-form] input[type="checkbox"]:checked').length > 0;
            if (hasFilters && productsAdded) {
                if (DEBUG) console.log('🔄 Product grid updated, re-filtering for stock...');
                debouncedFilter();
            }
        });

        observer.observe(productGrid, {
            childList: true,
            subtree: true  // Changed to true to catch nested changes
        });
    }

    // Check if lazysizes is available (silent check, no logging)
    function waitForLazySizes(callback) {
        if (typeof window.lazySizes !== 'undefined') {
            if (callback) callback();
        } else {
            setTimeout(function() { waitForLazySizes(callback); }, 100);
        }
    }

    // Initialize lazysizes check when available
    waitForLazySizes(function() {
        // lazysizes is ready, no action needed
    });
})();
